import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useApp } from "@/lib/AppContext";
import { ApiError, type MeetingProposal, type ReviewSegment, type TranscriptReview } from "@/lib/api";
import { formatDate, formatTime } from "@/lib/format";
import {
  cutAt,
  type DraftPart,
  mergeUp,
  moveStart,
  partDescription,
  partRanges,
  partReason,
  partTitle,
  partVoices,
  patchPart,
  peopleIn,
  speakerLookup,
  useMeetingParts,
} from "@/lib/meetingParts";
import { useReviewPlayer } from "@/lib/useReviewPlayer";
import { Header } from "@/pages/review/Header";
import { PartDivider, PartMap, type PartView, partOf } from "@/pages/review/PartMap";
import { PlayerBar } from "@/pages/review/PlayerBar";
import { Segment } from "@/pages/review/Segment";

/** Where the reading line is kept as the column scrolls under it. */
const READING_LINE_AT = 0.42;

/**
 * UI-12 — Transcript Review.
 *
 * A full-window reading and correction view for one recording, deliberately outside
 * the app shell: it is not a destination, it is what you do to a transcript, and a rail
 * down the side of it would invite you to leave in the middle of a sentence. The way
 * out is one control, top left, and Escape.
 *
 * Three things about how this is built are load-bearing, all of them consequences of
 * the same fact — an hour of speech is ten thousand words and a three-hour meeting is
 * thirty thousand.
 *
 * **The highlight is imperative.** React renders the words once; the moving parts — the
 * current word's background, the reading line, the scroll position — are moved by
 * hand on each animation frame. Re-rendering the column at frame rate would be the
 * whole budget, and it would buy nothing: the text does not change while it is being
 * read.
 *
 * **Each segment is memoized.** Flagging one segment must not re-render the other five
 * hundred, so the only props a segment gets are its own row, its own two booleans, and
 * callbacks that never change identity.
 *
 * **Position comes from the audio element.** Never from a counter advanced on a timer,
 * which drifts against the sound within a minute and is visibly wrong within five.
 */
export function TranscriptReviewPage() {
  const { id } = useParams<{ id: string }>();
  const { api } = useApp();
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Whether "Split here" appears on each turn — carried in through the link that opened
   * this screen rather than re-derived here, because the two links that mean "this
   * meeting has not had its content phase run yet" (the wizard's meeting row, and the
   * Transcript tab's own review card) already know it. Reached any other way — a
   * bookmark, a reload, the finished meeting's own Transcript tab — it is absent, and
   * absent means no, the same as a split offered on a meeting whose summary and insights
   * were already read off this exact span (RecordingDetail's own "Split here" rule).
   */
  const splittable = Boolean((location.state as { splittable?: boolean } | null)?.splittable);

  /**
   * SPCH-14 — `?parts` opens the whole recording with its proposed meetings drawn in as parts,
   * and `?parts=3` opens it at the third. In the address rather than in link state, unlike
   * `splittable`, because this is a place somebody reloads in the middle of: the draft it edits
   * is kept across a reload, and the mode that shows it has to be too.
   */
  const [searchParams] = useSearchParams();
  const partsParam = searchParams.get("parts");
  const partsMode = partsParam !== null;

  const [review, setReview] = useState<TranscriptReview | null>(null);
  const [proposal, setProposal] = useState<MeetingProposal | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [menu, setMenu] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  /**
   * Which segment is open for correction, and which of its words the caret should land
   * on and have selected.
   *
   * One editor, entered two ways. The pencil opens the sentence with nothing selected;
   * clicking a word opens the same sentence with that word selected, so the first letter
   * typed replaces it. What it is *not* is a field around the one word — the correction
   * somebody came to make is very often a word and a half ("Van Dijk" for "van dijk, "),
   * or the word before it too, and a box that ends at the word's edge makes them close
   * it and open another one. The caret is free the moment it arrives.
   *
   * Kept as segment-and-index rather than a key, so that the segment which owns it is
   * handed a number while every other segment is handed `null` — which is what keeps six
   * hundred memoized segments still when one editor opens.
   */
  const [editing, setEditing] = useState<{ segmentId: string; word: number | null } | null>(null);

  /**
   * The word somebody just fixed by hand, offered to the organization's vocabulary (0035).
   *
   * This screen is where the problem is *discovered*. Somebody reads "comments", knows the
   * room said "Common", and retypes it — and having done that they are the one person who
   * will ever know why, at the one moment they know it. Every other route to teaching the
   * system that word costs a trip to Entities, a decision about what type it is, and
   * remembering the word by the time they get there.
   *
   * Offered rather than done: an entity is the organization's shared context and a machine
   * has never been allowed to mint one (INS-2 is explicit that tagging does not), so this
   * puts the gesture one click away and leaves the click to a person.
   *
   * Kept as segment-and-word, the same shape `editing` uses and for the same reason: the
   * segment that owns it is handed a string and the other five hundred are handed `null`.
   */
  const [teach, setTeach] = useState<{ segmentId: string; word: string } | null>(null);

  /** The segment a "Split here" click opened the split form under, or `null` when none
   * is open. Kept as a segmentId, the same shape `menu` uses, for the same reason: the
   * segment that owns it is handed `true` and the other five hundred are handed `false`. */
  const [splitAt, setSplitAt] = useState<string | null>(null);

  const player = useReviewPlayer(api, id, review?.recording.durationMs ?? null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    setReview(null);
    setProblem(null);

    void api
      .getReview(id)
      .then((loaded) => {
        if (!cancelled) setReview(loaded);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setProblem(
          cause instanceof ApiError && cause.status === 404
            ? "There is no recording at this address."
            : "That transcript could not be loaded.",
        );
      });

    return () => {
      cancelled = true;
    };
  }, [api, id]);

  const segments = review?.segments ?? [];
  const speakers = useMemo(() => review?.speakers ?? [], [review]);

  // ------------------------------------------------------------------ parts (SPCH-14)

  useEffect(() => {
    if (!id || !partsMode) return;
    let cancelled = false;

    setProposal(null);
    void api
      .getMeetingProposal(id)
      .then((loaded) => {
        if (!cancelled) setProposal(loaded);
      })
      .catch(() => {
        // No proposal to draw is a plain review, which is still worth having open.
        if (!cancelled) setProposal({ status: "unavailable", canApply: false, stretches: [], model: null });
      });

    return () => {
      cancelled = true;
    };
  }, [api, id, partsMode]);

  const stretches = useMemo(
    () => (partsMode && proposal?.status === "ready" && proposal.stretches.length > 0 ? proposal.stretches : null),
    [partsMode, proposal],
  );
  const { parts, update } = useMeetingParts(id, stretches);
  const partsEditable = parts !== null && Boolean(proposal?.canApply);
  const nameOf = useMemo(() => speakerLookup(speakers), [speakers]);

  /**
   * Where each part sits in the column: the segment its divider is drawn above, and what the
   * divider says. The Server hands a part every segment starting at or after its start and
   * before the next part's, so that is the rule here too — a divider drawn anywhere else would
   * be a picture of a different cut than the one "Start processing" makes.
   *
   * Memoized on the draft and the transcript only: the page re-renders ten times a second
   * while playing, and a divider handed a fresh object on every tick is a divider that
   * re-renders on every tick.
   */
  const layout = useMemo(() => {
    if (!parts || !stretches) return null;

    const ranges = partRanges(parts, stretches);
    const anchors: number[] = [];
    let cursor = 0;

    ranges.forEach((range, position) => {
      while (position > 0 && cursor < segments.length && (segments[cursor]!.startsAtMs ?? 0) < range.startMs) cursor += 1;
      anchors.push(cursor);
    });

    const views: PartView[] = parts.map((part, position) => {
      const { startMs, endMs } = ranges[position]!;
      const first = anchors[position]!;
      const next = position + 1 < parts.length ? anchors[position + 1]! : segments.length;
      const previousStart = position > 0 ? ranges[position - 1]!.startMs : null;
      const nextStart = position + 1 < parts.length ? ranges[position + 1]!.startMs : null;

      const earlier = segments[first - 1];
      const later = segments[first + 1];

      return {
        position,
        count: parts.length,
        startMs,
        endMs,
        verdict: part.verdict,
        title: partTitle(part, stretches, nameOf),
        description: partDescription(part, stretches, nameOf),
        reason: partReason(parts, position, stretches, nameOf),
        people: peopleIn(partVoices(parts, position, stretches, segments, speakers), nameOf),
        empty: next <= first,
        // A line earlier: the turn before this part's first, if it still falls after the
        // previous part's start. A line later: the turn after it, if it falls before the next
        // part's — which also means a part is never moved out of its last line.
        earlier:
          position > 0
          && first - 1 > anchors[position - 1]!
          && earlier?.startsAtMs != null
          && previousStart !== null
          && earlier.startsAtMs > previousStart
            ? { sequence: earlier.sequence, atMs: earlier.startsAtMs }
            : null,
        later:
          position > 0 && next > first && later?.startsAtMs != null && (nextStart === null || later.startsAtMs < nextStart)
            ? { sequence: later.sequence, atMs: later.startsAtMs }
            : null,
      };
    });

    const byAnchor = new Map<number, PartView[]>();
    views.forEach((view, position) => {
      const at = anchors[position]!;
      byAnchor.set(at, [...(byAnchor.get(at) ?? []), view]);
    });

    return { views, byAnchor, anchors: new Set(anchors) };
  }, [nameOf, parts, segments, speakers, stretches]);

  /** Read by the callbacks below rather than closed over, so that typing a part's title does not
   * hand six hundred memoized segments a new `onSplit`. */
  const layoutRef = useRef(layout);
  layoutRef.current = layout;

  /** The start of the part just cut, so its divider opens with the caret in its title. */
  const [focusPartAt, setFocusPartAt] = useState<number | null>(null);

  /**
   * Every word of the recording in one flat array, with the millisecond it starts.
   *
   * Flat because finding "which word is being spoken now" happens sixty times a second
   * and has to be a binary search over one array rather than a walk through six hundred
   * segments. Rebuilt only when the transcript itself changes — a flag or a
   * reassignment does not touch it.
   */
  const words = useMemo(() => {
    const flat: { key: string; startMs: number }[] = [];

    for (const segment of segments) {
      segment.words.forEach((word, index) => {
        flat.push({ key: `${segment.id}:${index}`, startMs: word.startMs });
      });
    }

    return flat;
  }, [segments]);

  const flagged = useMemo(() => segments.filter((segment) => segment.flaggedAt !== null).length, [segments]);

  const column = useRef<HTMLDivElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const line = useRef<HTMLDivElement>(null);
  const dot = useRef<HTMLDivElement>(null);
  const current = useRef(-1);
  const activeSegment = useRef(0);

  /** Counts word corrections sent, so a re-read for an older one cannot land on a newer. */
  const corrections = useRef(0);

  /** Which word is being spoken at this instant. Binary search, so an hour costs the
   * same as a minute. */
  const wordAt = useCallback(
    (ms: number) => {
      let low = 0;
      let high = words.length - 1;
      let found = -1;

      while (low <= high) {
        const middle = (low + high) >> 1;
        if (words[middle]!.startMs <= ms) {
          found = middle;
          low = middle + 1;
        } else {
          high = middle - 1;
        }
      }

      return found;
    },
    [words],
  );

  // The imperative half. One class moved, one hairline repositioned, one scroll — and
  // only when the word actually changes, which at conversational speed is three times
  // a second rather than sixty.
  useEffect(() => {
    const paint = (seconds: number) => {
      const index = wordAt(seconds * 1000);
      if (index === current.current) return;

      const container = column.current;
      if (!container) return;

      container.querySelector<HTMLElement>('[data-current="1"]')?.removeAttribute("data-current");
      current.current = index;

      // A quoted attribute selector rather than an escaped one: the key is a uuid and a
      // number joined by a colon, which needs no escaping inside quotes — and `CSS.escape`
      // is not everywhere.
      const active =
        index < 0 ? null : container.querySelector<HTMLElement>(`[data-w="${words[index]!.key}"]`);

      if (line.current) line.current.style.opacity = active ? "1" : "0";
      if (!active) return;

      active.setAttribute("data-current", "1");
      activeSegment.current = Number(active.dataset["seg"] ?? 0);

      const wrapper = container.getBoundingClientRect();
      const word = active.getBoundingClientRect();

      if (line.current) line.current.style.top = `${word.bottom - wrapper.top + 4}px`;
      if (dot.current) dot.current.style.left = `${word.left - wrapper.left + word.width / 2}px`;

      const view = scroller.current;
      if (!view) return;

      const target = Math.max(
        0,
        view.scrollTop + (word.bottom - view.getBoundingClientRect().top) - view.clientHeight * READING_LINE_AT,
      );

      if (Math.abs(view.scrollTop - target) > 32) view.scrollTo({ top: target, behavior: "smooth" });
    };

    player.onFrame(paint);
    // Paint once on mount and after every seek, so the line is right before the first
    // frame of playback rather than after it.
    paint(player.time);

    return () => player.onFrame(null);
    // `player.time` is deliberately not a dependency: this effect installs a painter,
    // and reinstalling it ten times a second would be the re-render it exists to avoid.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player.onFrame, wordAt, words]);

  // ------------------------------------------------------------------ the four writes

  /** Replaces one segment in place, so React re-renders that segment and no other. */
  const patch = useCallback((segmentId: string, change: Partial<ReviewSegment>) => {
    setReview((previous) =>
      previous === null
        ? previous
        : {
            ...previous,
            segments: previous.segments.map((segment) =>
              segment.id === segmentId ? { ...segment, ...change } : segment,
            ),
          },
    );
  }, []);

  const say = useCallback((cause: unknown) => {
    setProblem(cause instanceof ApiError ? cause.message : "The Server could not be reached.");
  }, []);

  const toggleFlag = useCallback(
    (segmentId: string) => {
      const segment = segments.find((candidate) => candidate.id === segmentId);
      if (!segment || !id) return;

      const next = segment.flaggedAt === null;

      // Optimistic. Flagging is how somebody marks a place while listening, and a
      // round trip between the key and the mark is exactly long enough to lose it.
      patch(segmentId, { flaggedAt: next ? new Date().toISOString() : null });

      void api.flagSegment(id, segmentId, next).catch((cause: unknown) => {
        patch(segmentId, { flaggedAt: segment.flaggedAt });
        say(cause);
      });
    },
    [api, id, patch, say, segments],
  );

  const reassign = useCallback(
    (segmentId: string, speakerId: string | null) => {
      if (!id) return;

      const segment = segments.find((candidate) => candidate.id === segmentId);
      setMenu(null);
      patch(segmentId, { speakerId });

      void api.reassignSegment(id, segmentId, speakerId).catch((cause: unknown) => {
        patch(segmentId, { speakerId: segment?.speakerId ?? null });
        say(cause);
      });
    },
    [api, id, patch, say, segments],
  );

  /**
   * A correction, sent as what the Server stores — the segment's whole corrected text.
   *
   * Optimistic, because this is meant to be used a dozen times down a page: fix a word,
   * click into the next sentence, fix a word there. A round trip between the last
   * keystroke and the sentence reappearing is exactly long enough to break that rhythm,
   * so the corrected text goes on screen first.
   *
   * It goes on with no words at all, which is deliberate. A corrected segment's word
   * timings are the Server's arithmetic (`CorrectedWords`) and reproducing them here to
   * fill the gap would be this screen quietly holding a second opinion about when each
   * word was said. Without them the sentence simply draws as its own text for the
   * moment it takes the re-read to answer — right, unhighlightable, and honest about
   * which of the two it is.
   *
   * The ticket is what keeps two quick corrections from fighting. A re-read is applied
   * only when no newer correction has been sent since it was asked for — otherwise the
   * answer to "Van Dyke" would land on top of the "Van Dijk" typed after it.
   */
  const correct = useCallback(
    (segmentId: string, text: string) => {
      if (!id) return;

      const ticket = ++corrections.current;
      patch(segmentId, { text, words: [] });

      void (async () => {
        const reread = async () => {
          const fresh = await api.getReview(id);
          if (ticket === corrections.current) setReview(fresh);
        };

        try {
          await api.correctSegment(id, segmentId, text);
          if (ticket === corrections.current) await reread();
        } catch (cause) {
          say(cause);

          // The optimistic text is on screen and the Server does not have it. Re-read,
          // so that what is being read is what is stored — and if that fails too, leave
          // the first failure standing rather than replacing it with a second.
          await reread().catch(() => undefined);
        }
      })();
    },
    [api, id, patch, say],
  );

  const revert = useCallback(
    async (segmentId: string) => {
      if (!id) return;

      setEditing(null);

      const segment = segments.find((candidate) => candidate.id === segmentId);
      if (!segment?.correction) return;

      setSaving(true);
      try {
        await api.revertCorrection(id, segmentId);
        setReview(await api.getReview(id));
      } catch (cause) {
        say(cause);
      } finally {
        setSaving(false);
      }
    },
    [api, id, say, segments],
  );

  const resolveProposal = useCallback(
    async (correctionId: string, apply: boolean) => {
      if (!id) return;

      setSaving(true);
      try {
        await api.resolveProposal(id, correctionId, apply);

        // Re-read rather than patch: approving a proposal changes the segment's effective
        // text and its word timings (the Server's `CorrectedWords` arithmetic), the same
        // reason a human correction re-reads instead of guessing them here.
        setReview(await api.getReview(id));
      } catch (cause) {
        say(cause);
      } finally {
        setSaving(false);
      }
    },
    [api, id, say],
  );

  const markReviewed = useCallback(async () => {
    if (!id || !review) return;

    const next = review.recording.reviewedAt === null;

    try {
      const { reviewedAt } = await api.setReviewed(id, next);
      setReview((previous) =>
        previous === null ? previous : { ...previous, recording: { ...previous.recording, reviewedAt } },
      );
    } catch (cause) {
      say(cause);
    }
  }, [api, id, review, say]);

  // ------------------------------------------------------------------ editor & menu

  // Stable identities, because these are handed to six hundred memoized segments and
  // the page re-renders ten times a second while playing — an inline arrow here would
  // defeat the memo and reconcile every word span on every tick.
  /**
   * Opens a sentence for correction — on a word, when a word is what was clicked, and
   * taking the playhead there on the way.
   *
   * Clicking a word also seeks to it: the word somebody wants to correct is very often
   * the word they want to hear once more first. It pauses, parks the playhead on that word, and opens
   * the sentence with it selected — Escape and then space plays that exact moment back,
   * without anybody hunting for the place they were just reading.
   */
  const openEditor = useCallback(
    (segmentId: string, word: number | null = null, startMs?: number) => {
      player.pause();
      if (startMs !== undefined) player.seek(startMs / 1000 + 0.01);
      setMenu(null);
      setEditing({ segmentId, word });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [player.pause, player.seek],
  );

  /**
   * Closes the editor — the one named, when one is named.
   *
   * Scoped, because an editor on its way out can outlive the state that opened the next
   * one: the press that closes a sentence is the same press that opens another, and a
   * blind `null` here would shut the one that just arrived.
   */
  const closeEditor = useCallback((segmentId?: string) => {
    setEditing((open) =>
      open === null || segmentId === undefined || open.segmentId === segmentId ? null : open,
    );
  }, []);

  const openTeach = useCallback(
    (segmentId: string, word: string) => setTeach({ segmentId, word }),
    [],
  );

  const closeTeach = useCallback(() => setTeach(null), []);

  const toggleMenu = useCallback(
    (segmentId: string) => setMenu((open) => (open === segmentId ? null : segmentId)),
    [],
  );

  const toggleSplit = useCallback(
    (segmentId: string) => setSplitAt((open) => (open === segmentId ? null : segmentId)),
    [],
  );

  const submitSplit = useCallback(
    async (input: { title: string; description?: string; fromSequence: number }) => {
      if (!id) return;
      const created = await api.splitMeeting(id, input);
      navigate(`/recordings/${created.id}`);
    },
    [api, id, navigate],
  );

  /** SPCH-14 — "Start a new part here": a cut in the draft, not a meeting on the Server. */
  const cutHere = useCallback(
    (segmentId: string) => {
      const index = segments.findIndex((candidate) => candidate.id === segmentId);
      const segment = segments[index];
      // A turn that already opens a part has a divider above it to move or remove instead.
      if (!stretches || segment?.startsAtMs == null || index <= 0 || layoutRef.current?.anchors.has(index)) return;

      const point = { sequence: segment.sequence, atMs: segment.startsAtMs };
      update((current) => cutAt(current, stretches, point)?.parts ?? current);
      setFocusPartAt(point.atMs);
    },
    [segments, stretches, update],
  );

  const patchDraftPart = useCallback(
    (position: number, change: Partial<DraftPart>) => update((current) => patchPart(current, position, change)),
    [update],
  );

  const mergePart = useCallback((position: number) => update((current) => mergeUp(current, position)), [update]);

  const nudgePart = useCallback(
    (position: number, direction: "earlier" | "later") => {
      const view = layoutRef.current?.views[position];
      const point = view?.[direction];
      if (!stretches || !point) return;

      update((current) => moveStart(current, position, stretches, point));
    },
    [stretches, update],
  );

  /** Take the reader to a part: the playhead to its start, and its divider into view. */
  const jumpToPart = useCallback(
    (position: number) => {
      const view = layoutRef.current?.views[position];
      if (!view) return;

      player.seek(view.startMs / 1000 + 0.01);

      // Instant, and once more a frame later. Segments off screen are laid out at an estimated
      // height (`content-visibility`), so the first jump lands near the divider and the turns it
      // passes then take their real height; the second lands on it. A smooth scroll would be
      // cancelled by exactly that change in height partway through.
      const divider = column.current?.querySelector<HTMLElement>(`[data-part="${position}"]`);
      divider?.scrollIntoView?.({ block: "start" });
      requestAnimationFrame(() => divider?.scrollIntoView?.({ block: "start" }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [player.seek],
  );

  // Opened at a part (`?parts=3`, from its row in the table): go there once, as soon as there
  // is a divider to go to.
  const openedAtPart = useRef(false);
  useEffect(() => {
    if (openedAtPart.current || !layout || segments.length === 0) return;
    openedAtPart.current = true;

    const position = Number(partsParam) - 1;
    if (Number.isInteger(position) && position > 0 && position < layout.views.length) jumpToPart(position);
  }, [jumpToPart, layout, partsParam, segments.length]);

  // ------------------------------------------------------------------ keyboard

  const exit = useCallback(() => navigate(`/recordings/${id}`), [id, navigate]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      // A control that already answered the key — the scrubber's own arrow handling —
      // must not be answered a second time from here.
      if (event.defaultPrevented) return;

      const tag = (event.target as HTMLElement | null)?.tagName ?? "";
      // Typing a correction is typing, not shortcuts. Escape still gets through,
      // because leaving an editor is the one thing a keyboard user must always be able
      // to do, and the editor handles it itself.
      if (tag === "TEXTAREA" || tag === "INPUT") return;

      const at = () => segments[activeSegment.current]?.id;

      if (event.code === "Space") {
        // Space on a focused button activates the button; taking it over would make
        // every control on a keyboard-driven screen unpressable by keyboard.
        if (tag === "BUTTON") return;
        event.preventDefault();
        player.toggle();
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        player.nudge(-5);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        player.nudge(5);
      } else if (event.key === "f" || event.key === "F") {
        const segmentId = at();
        if (segmentId) toggleFlag(segmentId);
      } else if (event.key === "e" || event.key === "E") {
        const segmentId = at();
        if (segmentId) openEditor(segmentId);
      } else if ((event.key === "s" || event.key === "S") && partsEditable) {
        const segmentId = at();
        if (segmentId) cutHere(segmentId);
      } else if (event.key === "Escape") {
        // Closes what is open, and leaves when nothing is.
        if (menu) setMenu(null);
        else if (editing) setEditing(null);
        else exit();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // Individual player callbacks rather than the player object: the object is remade
    // every render, and the page renders ten times a second while playing — depending
    // on it would tear this listener down and rebuild it on every tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cutHere, editing, exit, menu, openEditor, partsEditable, player.nudge, player.toggle, segments, toggleFlag]);

  // ------------------------------------------------------------------ render

  if (problem && !review) {
    return (
      <div className="flex h-dvh flex-col items-center justify-center gap-[var(--space-4)] bg-[var(--surface-0)] text-center">
        <p className="text-[length:var(--text-lg)] font-semibold text-[var(--text-primary)]">{problem}</p>
        <Link to="/recordings" className="text-[length:var(--text-sm)] text-[var(--text-secondary)] underline">
          Back to recordings
        </Link>
      </div>
    );
  }

  if (!review) {
    return (
      <div className="flex h-dvh items-center justify-center bg-[var(--surface-0)] text-[length:var(--text-sm)] text-[var(--text-secondary)]">
        Loading the transcript…
      </div>
    );
  }

  const { recording } = review;
  const title = recording.title ?? recording.originalFilename ?? "Untitled recording";
  const reviewed = recording.reviewedAt !== null;

  return (
    <div className="relative flex h-dvh flex-col overflow-hidden bg-[var(--surface-0)] text-[var(--text-primary)]">
      {/* The two brand auras the handoff puts behind this screen. Decorative, and the
          only thing separating a reading view from a text file. */}
      <div
        aria-hidden
        className="pointer-events-none absolute -left-[120px] -top-[180px] h-[520px] w-[620px] rounded-full opacity-60"
        style={{ background: "radial-gradient(circle, color-mix(in oklch, var(--vl-bridge) 16%, transparent), transparent 70%)" }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-[200px] -right-[140px] h-[520px] w-[620px] rounded-full opacity-60"
        style={{ background: "radial-gradient(circle, color-mix(in oklch, var(--vl-accent-warm) 14%, transparent), transparent 70%)" }}
      />

      <Header
        title={title}
        meta={[
          formatDate(recording.sourceDate ?? recording.createdAt),
          formatTime(recording.startedAt),
          languageName(recording.language),
        ]
          .filter(Boolean)
          .join(" · ")}
        flagged={flagged}
        reviewed={reviewed}
        onExit={exit}
        onReviewed={() => void markReviewed()}
      />

      {layout && (
        <PartMap
          views={layout.views}
          durationMs={review.recording.durationMs ?? layout.views.at(-1)?.endMs ?? 0}
          currentMs={player.time * 1000}
          editable={partsEditable}
          onJump={jumpToPart}
          startedAt={recording.startedAt}
        />
      )}

      {problem && (
        <p
          role="alert"
          className="relative z-[3] bg-[var(--surface-1)] px-[var(--space-6)] py-[var(--space-2)] text-[length:var(--text-xs)] text-[var(--warning)]"
        >
          {problem}
        </p>
      )}

      <div className="relative z-[2] min-h-0 flex-1">
        <div ref={scroller} className="absolute inset-0 overflow-y-auto pt-[56px]">
          <div ref={column} className="relative mx-auto max-w-[820px] px-[var(--space-8)] pb-[45vh]">
            {segments.length === 0 && (
              <p className="text-center text-[length:var(--text-sm)] text-[var(--text-secondary)]">
                This recording has no transcript to read yet.
              </p>
            )}

            {segments.map((segment, index) => (
              <Fragment key={segment.id}>
                {layout?.byAnchor.get(index)?.map((view) => (
                  <PartDivider
                    key={`part-${view.startMs}`}
                    view={view}
                    editable={partsEditable}
                    autoFocus={focusPartAt === view.startMs}
                    onPatch={patchDraftPart}
                    onMerge={mergePart}
                    onNudge={nudgePart}
                    onSeek={player.seek}
                    startedAt={recording.startedAt}
                  />
                ))}
                <Segment
                  segment={segment}
                  index={index}
                  speakers={speakers}
                  startedAt={recording.startedAt}
                  editing={editing?.segmentId === segment.id}
                  editingWord={editing?.segmentId === segment.id ? editing.word : null}
                  teach={teach?.segmentId === segment.id ? teach.word : null}
                  menuOpen={menu === segment.id}
                  saving={saving}
                  // In parts mode a cut is a draft part, offered on every turn that does not
                  // already open one; otherwise it is SPCH-12's split, as it always was.
                  splittable={
                    layout
                      ? partsEditable && index > 0 && segment.startsAtMs !== null && !layout.anchors.has(index)
                      : splittable && index > 0
                  }
                  cutsParts={layout !== null}
                  discarded={layout ? partOf(layout.views, segment.startsAtMs)?.verdict === "discard" : false}
                  splitOpen={splitAt === segment.id}
                  onSeek={player.seek}
                  onFlag={toggleFlag}
                  onEdit={openEditor}
                  onCloseEditor={closeEditor}
                  onTeach={openTeach}
                  onCloseTeach={closeTeach}
                  onMenu={toggleMenu}
                  onReassign={reassign}
                  onCorrect={correct}
                  onRevert={revert}
                  onResolveProposal={resolveProposal}
                  onSplit={layout ? cutHere : toggleSplit}
                  onSubmitSplit={submitSplit}
                />
              </Fragment>
            ))}

            {layout?.byAnchor.get(segments.length)?.map((view) => (
              <PartDivider
                key={`part-${view.startMs}`}
                view={view}
                editable={partsEditable}
                autoFocus={focusPartAt === view.startMs}
                onPatch={patchDraftPart}
                onMerge={mergePart}
                onNudge={nudgePart}
                onSeek={player.seek}
                startedAt={recording.startedAt}
              />
            ))}

            {/* The reading line. Positioned by hand every time the word changes, which
                is why it lives outside the segment list rather than inside a segment. */}
            <div
              ref={line}
              aria-hidden
              className="pointer-events-none absolute left-[var(--space-8)] right-[var(--space-8)] z-[4] h-0 opacity-0 transition-[top] duration-[150ms] ease-[cubic-bezier(0.4,0,0.2,1)]"
            >
              <div
                className="h-[2px] rounded-[2px]"
                style={{
                  background:
                    "linear-gradient(90deg, transparent, color-mix(in oklch, var(--vl-bridge) 55%, transparent) 10%, color-mix(in oklch, var(--vl-accent-warm) 85%, transparent) 50%, color-mix(in oklch, var(--vl-life) 50%, transparent) 90%, transparent)",
                }}
              />
              <div
                ref={dot}
                className="absolute -top-[4px] -ml-[4px] h-[9px] w-[9px] rounded-full bg-[var(--vl-accent-warm)] transition-[left] duration-[150ms] ease-[cubic-bezier(0.4,0,0.2,1)]"
                style={{ boxShadow: "0 0 14px 3px color-mix(in oklch, var(--vl-accent-warm) 55%, transparent)" }}
              />
            </div>
          </div>
        </div>

        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 z-[5] h-[60px]"
          style={{ background: "linear-gradient(to bottom, var(--surface-0), transparent)" }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 z-[5] h-[90px]"
          style={{ background: "linear-gradient(to top, var(--surface-0), transparent)" }}
        />
      </div>

      <PlayerBar
        player={player}
        segments={segments}
        speakers={speakers}
        wordStarts={words}
        duration={review.recording.durationMs}
        cutShortcut={partsEditable}
      />
    </div>
  );
}

// ------------------------------------------------------------------ header

/**
 * "nl" → "Dutch". Only the two the app actually handles today (SPCH-1); anything else
 * is shown as it arrived rather than guessed at.
 */
function languageName(code: string | null): string {
  if (!code) return "";

  const known: Record<string, string> = { nl: "Dutch", en: "English" };
  return known[code.toLowerCase()] ?? code.toUpperCase();
}
